import { Box, HStack, Skeleton, VStack } from "@chakra-ui/react";
import { format } from "date-fns";
import { CDS } from "design-system";
import SideDrawer from "components/SideDrawer";
import { useGroupQuery } from "../hooks";
import type { Group } from "../types";
import GroupTypeTag from "./GroupTypeTag";

interface GroupDetailDrawerProps {
  group: Group | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export default function GroupDetailDrawer({ group, isOpen, onClose }: GroupDetailDrawerProps) {
  // apiClient.get unwraps the `{ data: ... }` envelope, so `detail` here is the
  // GroupDetail directly (NOT `{ data: GroupDetail }`).
  const { data: detail, isLoading } = useGroupQuery(group?.id);

  return (
    <SideDrawer<Group>
      isOpen={isOpen}
      onClose={onClose}
      item={group}
      isLoading={isLoading}
      header={({ item }) => (
        <VStack align="start" spacing={1}>
          <CDS.Text18 fontWeight="semibold">{item?.name}</CDS.Text18>
          <HStack spacing={1}>
            {item && <GroupTypeTag groupType={item.groupType} />}
            {item?.scimProvisioned ? (
              <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.primary}>SCIM</CDS.Tag>
            ) : (
              <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>Manual</CDS.Tag>
            )}
          </HStack>
        </VStack>
      )}
    >
      {({ item, isLoading: drawerLoading }) => (
        <VStack align="stretch" spacing={6}>
          {item?.scimExternalId && (
            <Box>
              <CDS.Text12 color="fg.subtle" fontWeight="medium">External ID</CDS.Text12>
              <CDS.Text14>{item.scimExternalId}</CDS.Text14>
            </Box>
          )}
          {item?.scimLastSyncedAt && (
            <Box>
              <CDS.Text12 color="fg.subtle" fontWeight="medium">Last synced</CDS.Text12>
              <CDS.Text14>{format(new Date(item.scimLastSyncedAt), "MMM d, yyyy 'at' h:mm a")}</CDS.Text14>
            </Box>
          )}

          <Box>
            <CDS.Text12 color="fg.subtle" fontWeight="medium" mb={2}>
              {`Members (${item?.memberCount ?? 0})`}
            </CDS.Text12>

            {drawerLoading ? (
              <VStack align="stretch" spacing={2}>
                <Skeleton height="40px" />
                <Skeleton height="40px" />
                <Skeleton height="40px" />
              </VStack>
            ) : (detail?.users ?? []).length === 0 ? (
              <CDS.Text14 color="fg.subtle">No members yet.</CDS.Text14>
            ) : (
              <VStack align="stretch" spacing={2}>
                {detail?.users.map((user) => (
                  <CDS.UserTag
                    key={user.id}
                    name={user.fullName}
                    avatarUrl={user.avatarUrl ?? undefined}
                    showAvatar
                  />
                ))}
              </VStack>
            )}
          </Box>
        </VStack>
      )}
    </SideDrawer>
  );
}
